import { UserIdSchema } from "@core/domains/identity";
import {
  OrderKeySchema,
  createModel,
  createModelId,
  createModelSchemas,
  generateOrderKey,
  noModelUpdate,
  recoverOrderKeyFromId,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { FolderStructureSchema } from "./folder-structure.ts";
import { ID_PREFIX } from "./shared/id-prefix.ts";
import { TaxonomyIdSchema } from "./shared/taxonomy-id.ts";

const {
  generateId: generateFolderStructureRevisionId,
  idSchema: FolderStructureRevisionIdSchema,
} = createModelId({ idPrefix: ID_PREFIX.FOLDER_STRUCTURE_REVISION });

export { FolderStructureRevisionIdSchema };

export type FolderStructureRevisionId = z.infer<
  typeof FolderStructureRevisionIdSchema
>;

export const FolderStructureRevision = createModel({
  create: (item, schema) => {
    return {
      ...schema.decode(item),
      id: generateFolderStructureRevisionId(),
      orderKey: generateOrderKey(),
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        folderStructure: FolderStructureSchema,
        id: FolderStructureRevisionIdSchema,
        orderKey: OrderKeySchema,
        principalId: UserIdSchema,
        taxonomyId: TaxonomyIdSchema,
      },
      {
        folderStructure: "The taxonomy's folder structure as of this revision.",
        id: "The revision's unique identifier.",
        orderKey: "The key ordering the revisions by save, and nothing else.",
        principalId: "The id of the user the revision belongs to.",
        taxonomyId: "The id of the taxonomy the revision belongs to.",
      },
    ),
    derive: {
      create: schema => schema.omit({ id: true, orderKey: true }),
      // oxlint-disable-next-line no-warning-comments
      // TODO(APT-2405): drop once the stored records carry their orderKey.
      recovery: recoverOrderKeyFromId,
      update: noModelUpdate,
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface FolderStructureRevision
  extends z.infer<typeof FolderStructureRevision.schema> {}
